<template>
  <div v-if="currentUser && currentUser.role === 'admin'">

    <!-- ===== FULLSCREEN MODE ===== -->
    <div
      v-if="isFullscreen"
      class="fixed inset-0 flex flex-col"
      style="z-index: 10000; background: #0a0a0a"
    >
      <!-- Top bar with close / new chat -->
      <div class="flex-shrink-0 flex items-center justify-between px-5 pt-4 pb-2">
        <button
          @click="startNewChat"
          class="text-gray-500 hover:text-gray-300 text-sm px-2 py-1 rounded transition-colors"
          title="New chat"
        >
          <i class="fa-solid fa-plus mr-1"></i> New
        </button>
        <div class="text-xs" :class="statusClass">{{ statusLabel }}</div>
        <div class="flex items-center gap-1">
          <button
            @click="toggleBgMusic"
            class="text-gray-500 hover:text-gray-300 text-sm px-2 py-1 rounded transition-colors"
            :title="bgMusicMuted ? 'Unmute music' : 'Mute music'"
          >
            <i :class="bgMusicMuted ? 'fa-solid fa-volume-xmark' : 'fa-solid fa-volume-high'" class="text-sm"></i>
          </button>
          <button
            @click="closeFullscreen"
            class="text-gray-500 hover:text-gray-300 text-sm px-2 py-1 rounded transition-colors"
            title="Close"
          >
            <i class="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>
      </div>

      <!-- Particle ball hero area -->
      <div class="flex-shrink-0 flex flex-col items-center justify-center" :class="messages.length === 0 ? 'py-8' : 'py-4'">
        <div
          ref="headerCanvas"
          class="rounded-full bg-black overflow-hidden"
          :style="{ width: ballSize + 'px', height: ballSize + 'px' }"
        ></div>
        <div v-if="messages.length === 0" class="mt-4 text-center">
          <h1 class="text-xl font-semibold text-white mb-1">KCT Assistant</h1>
          <p class="text-gray-500 text-sm">Ask me anything about jobs, customers, fleet, and more.</p>
        </div>
      </div>

      <!-- Chat area -->
      <div class="flex-grow flex flex-col min-w-0 overflow-hidden">

        <!-- Empty state suggestions -->
        <div v-if="messages.length === 0" class="flex-grow flex flex-col items-center justify-start px-6 pt-4 overflow-y-auto">
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-2xl w-full">
            <button
              v-for="(s, i) in suggestions"
              :key="i"
              @click="sendSuggestion(s.text)"
              class="text-left p-4 rounded-xl border border-gray-800 hover:border-gray-600 transition-all group"
              style="background: #141414"
            >
              <i :class="s.icon" class="text-primary text-sm mb-2 block"></i>
              <div class="text-sm font-semibold text-gray-300 mb-1 group-hover:text-primary transition-colors">{{ s.label }}</div>
              <div class="text-xs text-gray-600 leading-relaxed">{{ s.text }}</div>
            </button>
          </div>
        </div>

        <!-- Messages scroll area -->
        <div v-else ref="messagesContainer" class="flex-grow overflow-y-auto">
          <div class="max-w-3xl mx-auto py-6 px-4 sm:px-8">

            <div
              v-for="(msg, idx) in displayMessages"
              :key="idx"
              class="mb-6 text-left"
            >
              <!-- User message -->
              <div v-if="msg.role === 'user'" class="flex justify-end">
                <div class="max-w-xl px-5 py-3 rounded-2xl rounded-br-md text-sm leading-relaxed whitespace-pre-wrap text-white" style="background: #1e1e1e">{{ msg.content }}</div>
              </div>

              <!-- Assistant message -->
              <div v-else class="flex gap-3">
                <div class="w-7 h-7 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-1">
                  <i class="fa-solid fa-robot text-white" style="font-size: 10px"></i>
                </div>
                <div class="flex-1 min-w-0 rounded-2xl rounded-tl-md px-5 py-4" style="background: #141414; border: 1px solid #1e1e1e">
                  <div class="ai-msg-content text-sm text-gray-300 leading-relaxed" v-html="formatMessage(msg.content)"></div>
                </div>
              </div>
            </div>

            <!-- Tool status -->
            <div v-if="toolStatus" class="mb-6 flex gap-3 text-left">
              <div class="w-7 h-7 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-1">
                <i class="fa-solid fa-robot text-white" style="font-size: 10px"></i>
              </div>
              <div class="flex-1">
                <div class="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm text-green-400" style="background: #0d1f0d; border: 1px solid #1a3a1a">
                  <svg class="animate-spin h-3.5 w-3.5 text-green-400" viewBox="0 0 24 24" fill="none">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  {{ toolStatus }}
                </div>
              </div>
            </div>

            <!-- Thinking dots -->
            <div v-if="loading && !streamingContent && !toolStatus" class="mb-6 flex gap-3 text-left">
              <div class="w-7 h-7 rounded-full bg-primary flex items-center justify-center flex-shrink-0 mt-1">
                <i class="fa-solid fa-robot text-white" style="font-size: 10px"></i>
              </div>
              <div class="flex-1">
                <div class="inline-flex items-center gap-1.5 rounded-xl px-4 py-3" style="background: #141414; border: 1px solid #1e1e1e">
                  <span class="w-2 h-2 bg-gray-600 rounded-full animate-bounce" style="animation-delay: 0ms"></span>
                  <span class="w-2 h-2 bg-gray-600 rounded-full animate-bounce" style="animation-delay: 150ms"></span>
                  <span class="w-2 h-2 bg-gray-600 rounded-full animate-bounce" style="animation-delay: 300ms"></span>
                </div>
              </div>
            </div>

          </div>
        </div>

        <!-- Input area -->
        <div class="flex-shrink-0 px-4 sm:px-8 py-4" style="background: #0a0a0a">
          <div class="max-w-3xl mx-auto">
            <div class="flex items-end gap-3 rounded-2xl px-4 py-3 transition-all" style="background: #141414; border: 1px solid #2a2a2a">
              <textarea
                ref="inputArea"
                v-model="input"
                @keydown="handleKeydown"
                placeholder="Message KCT Assistant..."
                :disabled="loading"
                rows="1"
                class="flex-1 bg-transparent border-none outline-none resize-none text-sm text-gray-200 placeholder-gray-600 leading-relaxed max-h-32"
                style="font-family: inherit; padding: 2px 0"
              ></textarea>
              <button
                @click="send"
                :disabled="loading || !input.trim()"
                class="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm transition-all"
                :class="loading || !input.trim() ? 'bg-gray-800 text-gray-600 cursor-not-allowed' : 'bg-primary hover:bg-green-600 cursor-pointer'"
              >
                <i v-if="loading" class="fa-solid fa-stop text-xs"></i>
                <i v-else class="fa-solid fa-arrow-up text-sm"></i>
              </button>
            </div>
            <div class="text-center text-xs text-gray-700 mt-2.5">
              KCT Assistant can make mistakes. Always verify important data.
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import { getAuth } from "firebase/auth";
import ParticleBall from "./assistant/ParticleBall.js";
import ElevenLabsAudio from "./assistant/elevenlabs-audio.js";
import bgMusicSrc from "./assistant/bgmusic.mp3";

const API_BASE = "https://us-central1-ktc-app-33f33.cloudfunctions.net/widgets";
const OPENAI_API_KEY = process.env.VUE_APP_OPENAI_API_KEY || "";

export default {
  name: "ChatWidget",
  data() {
    return {
      isFullscreen: false,
      assistantState: "idle",
      bgMusicMuted: localStorage.getItem("kct_bg_music_muted") === "true",
      input: "",
      loading: false,
      messages: [],
      history: [],
      streamingContent: "",
      toolStatus: "",
      conversations: [],
      activeConversationIndex: -1,
      suggestions: [
        { icon: "fa-solid fa-calendar-day", label: "Today's Jobs", text: "What jobs do we have today?" },
        { icon: "fa-solid fa-truck", label: "Fleet Status", text: "Show me the fleet summary" },
        { icon: "fa-solid fa-chart-line", label: "Dashboard", text: "Give me a dashboard summary for this week" },
        { icon: "fa-solid fa-bolt", label: "CRM Pipeline", text: "Show me the CRM pipeline summary" },
        { icon: "fa-solid fa-users", label: "Tester Workloads", text: "Who has the most jobs this week?" },
        { icon: "fa-solid fa-magnifying-glass", label: "Search Jobs", text: "Search for jobs in Coomera this week" },
      ],
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    displayMessages() {
      const msgs = [...this.messages];
      if (this.streamingContent) {
        msgs.push({ role: "assistant", content: this.streamingContent });
      }
      return msgs;
    },
    statusLabel() {
      if (this.assistantState === "thinking") return "Thinking...";
      if (this.assistantState === "speaking") return "Speaking";
      return "Online";
    },
    statusClass() {
      if (this.assistantState === "thinking") return "text-orange-400";
      if (this.assistantState === "speaking") return "text-green-400";
      return "text-gray-600";
    },
    ballSize() {
      if (!this.isFullscreen) return 64;
      return this.messages.length === 0 ? 120 : 64;
    },
  },
  watch: {
    displayMessages() {
      this.$nextTick(() => this.scrollToBottom());
    },
    toolStatus() {
      this.$nextTick(() => this.scrollToBottom());
    },
    input() {
      this.$nextTick(() => this.autoResizeTextarea());
    },
    ballSize(newSize) {
      this.$nextTick(() => {
        if (this.isFullscreen && this.$refs.headerCanvas) {
          this._moveCanvasTo(this.$refs.headerCanvas, newSize, newSize);
        }
      });
    },
  },
  mounted() {
    this.particleBall = new ParticleBall();
    this.elevenLabs = new ElevenLabsAudio();

    // Background music setup — separate from TTS audio
    this.bgMusic = new Audio(bgMusicSrc);
    this.bgMusic.loop = true;
    this.bgMusic.volume = 0.15;

    this.elevenLabs.onStateChange((isSpeaking, analyser) => {
      if (isSpeaking) {
        this.assistantState = "speaking";
        this.particleBall.setState("speaking");
        this.particleBall.setAudioAnalyser(analyser);
      } else {
        this.assistantState = "idle";
        this.particleBall.setState("idle");
        this.particleBall.setAudioAnalyser(null);
      }
    });

    this.loadConversationsFromStorage();

    // Init particle ball in mini container
    this.$nextTick(() => {
      if (this.$refs.miniCanvas) {
        this.particleBall.init(this.$refs.miniCanvas);
      }
    });

    // Listen for open event from AiAssistant redirect
    this.$root.$on("open-chat-widget", this.openFullscreen);
  },
  beforeDestroy() {
    this.$root.$off("open-chat-widget", this.openFullscreen);
    if (this.bgMusic) { this.bgMusic.pause(); this.bgMusic = null; }
    if (this.particleBall) this.particleBall.destroy();
    if (this.elevenLabs) this.elevenLabs.destroy();
  },
  methods: {
    openFullscreen() {
      this.isFullscreen = true;
      this.$nextTick(() => {
        const size = this.ballSize;
        this._moveCanvasTo(this.$refs.headerCanvas, size, size);
        if (this.$refs.inputArea) this.$refs.inputArea.focus();
        // Start background music if not muted
        if (this.bgMusic && !this.bgMusicMuted) {
          this.bgMusic.play().catch(() => {});
        }
      });
    },

    closeFullscreen() {
      this.isFullscreen = false;
      // Pause background music when closing
      if (this.bgMusic) { this.bgMusic.pause(); }
      this.$nextTick(() => {
        this._moveCanvasTo(this.$refs.miniCanvas, 64, 64);
      });
    },

    toggleBgMusic() {
      this.bgMusicMuted = !this.bgMusicMuted;
      localStorage.setItem("kct_bg_music_muted", this.bgMusicMuted ? "true" : "false");
      if (this.bgMusic) {
        if (this.bgMusicMuted) {
          this.bgMusic.pause();
        } else {
          this.bgMusic.play().catch(() => {});
        }
      }
    },

    _moveCanvasTo(container, w, h) {
      if (!this.particleBall || !container) return;
      const canvas = this.particleBall.getCanvas();
      if (canvas && canvas.parentNode !== container) {
        container.appendChild(canvas);
      }
      this.particleBall.resize(w, h);
    },

    scrollToBottom() {
      const c = this.$refs.messagesContainer;
      if (c) c.scrollTop = c.scrollHeight;
    },

    autoResizeTextarea() {
      const ta = this.$refs.inputArea;
      if (!ta) return;
      ta.style.height = "auto";
      ta.style.height = Math.min(ta.scrollHeight, 128) + "px";
    },

    handleKeydown(e) {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        this.send();
      }
    },

    // Ask GPT to produce a short spoken summary of the assistant response
    async _generateVoiceSummary(text) {
      if (!text || !OPENAI_API_KEY) return "";
      try {
        const res = await fetch("https://api.openai.com/v1/chat/completions", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${OPENAI_API_KEY}`,
          },
          body: JSON.stringify({
            model: "gpt-4o-mini",
            max_tokens: 200,
            temperature: 0.7,
            messages: [
              {
                role: "system",
                content:
                  "You are a voice summary assistant. The user will give you a detailed text response from an AI assistant. " +
                  "Your job is to rewrite it as a brief, natural, conversational spoken summary (1-3 sentences). " +
                  "Do NOT read out tables, lists of data, or column values — instead say something like \"I've laid out the details in a table for you\" or \"here's the breakdown below\". " +
                  "Mention only the key highlights and numbers. Sound like a helpful colleague talking, not reading a document. " +
                  "Never use markdown, bullet points, or any formatting. Just plain spoken English.",
              },
              { role: "user", content: text },
            ],
          }),
        });
        if (!res.ok) {
          console.warn("[VoiceSummary] GPT call failed:", res.status);
          return "";
        }
        const json = await res.json();
        return (json.choices[0].message.content || "").trim();
      } catch (e) {
        console.warn("[VoiceSummary] error:", e);
        return "";
      }
    },

    formatMessage(text) {
      if (!text) return "";
      let html = text
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

      // Tables
      html = html.replace(
        /((?:^|\n)\|.+\|(?:\n\|[-:| ]+\|)?(?:\n\|.+\|)+)/g,
        (match) => {
          const rows = match.trim().split("\n").filter((r) => r.trim());
          const isSep = (r) => /^\|[\s-:|]+\|$/.test(r.trim());
          let table = '<table class="ai-tbl">';
          let inHeader = true;
          for (const row of rows) {
            if (isSep(row)) { inHeader = false; continue; }
            const cells = row.split("|").filter((_, i, a) => i > 0 && i < a.length - 1);
            const tag = inHeader ? "th" : "td";
            table += "<tr>" + cells.map((c) => `<${tag}>${c.trim()}</${tag}>`).join("") + "</tr>";
            if (inHeader && !rows.some(isSep)) inHeader = false;
          }
          table += "</table>";
          return table;
        }
      );

      html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
      html = html.replace(/\*(.*?)\*/g, "<em>$1</em>");
      html = html.replace(/`([^`]+)`/g, '<code style="background:#1e1e1e;padding:1px 5px;border-radius:4px;font-size:12px;font-family:monospace;color:#f472b6">$1</code>');
      html = html.replace(/\n/g, "<br>");

      return html;
    },

    async getAuthToken() {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) return null;
      return user.getIdToken();
    },

    loadConversationsFromStorage() {
      try {
        const saved = localStorage.getItem("kct_ai_conversations");
        if (saved) this.conversations = JSON.parse(saved);
      } catch (_) { /* ignore */ }
    },

    saveConversationsToStorage() {
      try {
        localStorage.setItem("kct_ai_conversations", JSON.stringify(this.conversations));
      } catch (_) { /* ignore */ }
    },

    startNewChat() {
      this.saveCurrentConversation();
      this.messages = [];
      this.history = [];
      this.streamingContent = "";
      this.toolStatus = "";
      this.activeConversationIndex = -1;
      this.elevenLabs.stop();
      this.$nextTick(() => {
        if (this.$refs.inputArea) this.$refs.inputArea.focus();
      });
    },

    saveCurrentConversation() {
      if (this.messages.length === 0) return;
      const firstUserMsg = this.messages.find((m) => m.role === "user");
      const title = firstUserMsg
        ? firstUserMsg.content.substring(0, 50) + (firstUserMsg.content.length > 50 ? "..." : "")
        : "New conversation";
      const conv = { title, messages: [...this.messages], history: [...this.history] };
      if (this.activeConversationIndex >= 0) {
        this.conversations.splice(this.activeConversationIndex, 1, conv);
      } else {
        this.conversations.unshift(conv);
        this.activeConversationIndex = 0;
      }
      this.saveConversationsToStorage();
    },

    loadConversation(idx) {
      this.saveCurrentConversation();
      const conv = this.conversations[idx];
      this.messages = [...conv.messages];
      this.history = [...(conv.history || [])];
      this.activeConversationIndex = idx;
      this.streamingContent = "";
      this.toolStatus = "";
      this.$nextTick(() => this.scrollToBottom());
    },

    deleteConversation(idx) {
      this.conversations.splice(idx, 1);
      if (this.activeConversationIndex === idx) {
        this.messages = [];
        this.history = [];
        this.activeConversationIndex = -1;
      } else if (this.activeConversationIndex > idx) {
        this.activeConversationIndex--;
      }
      this.saveConversationsToStorage();
    },

    sendSuggestion(text) {
      this.input = text;
      this.send();
    },

    async send() {
      const text = this.input.trim();
      if (!text || this.loading) return;

      this.input = "";
      this.$nextTick(() => this.autoResizeTextarea());
      this.messages.push({ role: "user", content: text });
      this.loading = true;
      this.streamingContent = "";
      this.toolStatus = "";

      // Set thinking state
      this.assistantState = "thinking";
      this.particleBall.setState("thinking");

      if (this.activeConversationIndex === -1 && this.messages.length === 1) {
        this.conversations.unshift({ title: text.substring(0, 50), messages: [], history: [] });
        this.activeConversationIndex = 0;
      }

      try {
        const token = await this.getAuthToken();
        if (!token) {
          this.messages.push({ role: "assistant", content: "Sorry, I couldn't verify your login. Please refresh the page." });
          return;
        }

        // Build QLD (AEST UTC+10, no DST) timestamp for context
        const now = new Date();
        const qld = new Date(now.getTime() + (10 * 60 - now.getTimezoneOffset()) * 60000);
        const days = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
        const qldStr = days[qld.getUTCDay()] + " " + qld.toISOString().slice(0, 16).replace("T", " ");
        const messageWithTime = `[Current date/time in QLD Australia: ${qldStr} AEST]\n\n${text}`;

        const response = await fetch(`${API_BASE}/api/v1/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ message: messageWithTime, history: this.history }),
        });

        const contentType = response.headers.get("content-type") || "";

        if (contentType.includes("text/event-stream")) {
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() || "";

            for (const line of lines) {
              if (!line.startsWith("data: ")) continue;
              let data;
              try { data = JSON.parse(line.slice(6)); } catch (_) { continue; }

              switch (data.type) {
                case "text":
                  this.streamingContent += data.content;
                  break;
                case "tool_start":
                  this.toolStatus = data.label || data.tool;
                  break;
                case "tool_done":
                  this.toolStatus = "";
                  break;
                case "done":
                  if (this.streamingContent) {
                    const fullResponse = this.streamingContent;
                    this.messages.push({ role: "assistant", content: fullResponse });
                    this.streamingContent = "";
                    // Ask GPT to write a spoken summary, then speak it
                    this._generateVoiceSummary(fullResponse).then((spoken) => {
                      if (spoken) this.elevenLabs.queueSpeak(spoken);
                    });
                  }
                  this.history = data.history || [];
                  break;
                case "error":
                  this.streamingContent += "\n\nSorry, something went wrong.";
                  break;
              }
            }
          }

          if (this.streamingContent) {
            const fullResponse = this.streamingContent;
            this.messages.push({ role: "assistant", content: fullResponse });
            this.streamingContent = "";
            this._generateVoiceSummary(fullResponse).then((spoken) => {
              if (spoken) this.elevenLabs.queueSpeak(spoken);
            });
          }
        } else {
          const json = await response.json();
          const data = json.data || json;
          const responseText = data.response || "No response received.";
          this.messages.push({ role: "assistant", content: responseText });
          this.history = data.history || [];
          this._generateVoiceSummary(responseText).then((spoken) => {
            if (spoken) this.elevenLabs.queueSpeak(spoken);
          });
        }
      } catch (e) {
        console.error("Chat error:", e);
        if (this.streamingContent) {
          this.messages.push({ role: "assistant", content: this.streamingContent + "\n\n(Connection interrupted)" });
          this.streamingContent = "";
        } else {
          this.messages.push({ role: "assistant", content: "Sorry, something went wrong. Please try again." });
        }
      } finally {
        this.loading = false;
        this.toolStatus = "";
        this.streamingContent = "";
        // Return to idle if not speaking (TTS callback handles speaking state)
        if (this.assistantState === "thinking") {
          this.assistantState = "idle";
          this.particleBall.setState("idle");
        }
        this.saveCurrentConversation();
      }
    },
  },
};
</script>

<style>
/* Dark-theme table styles for AI messages (unscoped for v-html) */
.ai-msg-content {
  word-break: break-word;
  text-align: left;
}
.ai-msg-content .ai-tbl {
  border-collapse: collapse;
  font-size: 13px;
  margin: 12px 0;
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #2a2a2a;
}
.ai-msg-content .ai-tbl th,
.ai-msg-content .ai-tbl td {
  border: 1px solid #2a2a2a;
  padding: 8px 12px;
  text-align: left;
}
.ai-msg-content .ai-tbl th {
  background: #1a1a1a;
  font-weight: 600;
  color: #d1d5db;
  font-size: 12px;
}
.ai-msg-content .ai-tbl td {
  color: #9ca3af;
}
.ai-msg-content .ai-tbl tr:hover td {
  background: #1a1a1a;
}
.ai-msg-content strong {
  color: #f3f4f6;
  font-weight: 600;
}
</style>
